/**
 * MiniCart — Persistent cart sidebar for the Tool Finder Results page
 *
 * Desktop: right-side panel alongside the product grid, matching the
 * live MRT UAT app behaviour. Users can add items from any product tab
 * without losing context or navigating away.
 *
 * Mobile: hidden entirely — the FloatingCartFAB handles mobile cart access.
 *
 * Item display ported from the legacy `lwc/miniCart/miniCart.html:11-24`
 * (image, product name, part number, "Remove item" link). Layout adapted
 * to fit a narrow sidebar column (~280px) rather than the legacy full-width
 * Lightning sticky component.
 *
 * Uses: CartContext (state.items, dispatch REMOVE_ITEM)
 *       AuthContext (permissions — price visibility gating)
 *
 * @module components/layout/MiniCart
 * @since 2026-05-28 (Group G phase 2 — G4)
 */

import { useNavigate } from 'react-router-dom';
import { ShoppingCart, Trash2, ImageOff } from 'lucide-react';
import { useCart } from '@contexts/CartContext';
import useAuth from '@hooks/useAuth';

/* ── Single cart item row ──────────────────────────────────────────── */

function MiniCartItem({ item, onRemove }) {
  const sku = item.itemCode || item.sku || item.productId;
  const name = item.name || item.description || sku;
  const imgSrc = item.thumbnailUrl || item.imageUrl;

  return (
    <div
      className="flex gap-3 py-3 border-b border-n2 last:border-b-0"
      data-testid="minicart-item"
      data-item-id={item.productId}
    >
      {/* Product thumbnail */}
      {imgSrc ? (
        <img
          src={imgSrc}
          alt={name}
          className="w-12 h-12 object-contain bg-n1 flex-shrink-0"
          loading="lazy"
        />
      ) : (
        <div className="w-12 h-12 bg-n1 flex items-center justify-center flex-shrink-0">
          <ImageOff size={16} className="text-n3" />
        </div>
      )}

      {/* Details */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-sandvik-text font-normal text-carbon-black line-clamp-2 leading-tight">
          {name}
        </p>
        <p className="text-xs text-n5 font-sandvik-text mt-0.5">
          {sku}
        </p>
        {item.quantity > 1 && (
          <p className="text-xs text-n6 font-sandvik-text mt-0.5">
            Qty: {item.quantity}
          </p>
        )}
        <button
          type="button"
          onClick={() => onRemove(item.productId)}
          className="text-xs text-carbon-black underline font-sandvik-text mt-1 hover:text-electric-blue transition-colors"
        >
          Remove item
        </button>
      </div>
    </div>
  );
}

/* ── Empty state ───────────────────────────────────────────────────── */

function EmptyState() {
  return (
    <div
      className="flex flex-col items-center justify-center py-10 text-center"
      data-testid="minicart-empty"
    >
      <ShoppingCart size={32} className="text-n3 mb-3" strokeWidth={1.5} />
      <p className="text-sm text-n5 font-sandvik-text font-normal">
        Your cart is empty
      </p>
      <p className="text-xs text-n4 font-sandvik-text mt-1">
        Add items from the product list
      </p>
    </div>
  );
}

/* ── Main MiniCart component ───────────────────────────────────────── */

export default function MiniCart() {
  const { state, dispatch, itemCount } = useCart();
  const { permissions } = useAuth();
  const navigate = useNavigate();

  const items = state.items;
  const isEmpty = items.length === 0;

  // Prices visible only when auth permissions include 'view_prices'
  // (Group A AuthContext role model).
  const showPrices =
    Array.isArray(permissions) && permissions.includes('view_prices');
  const subtotal = showPrices
    ? items.reduce(
        (sum, i) => sum + ((i.price || i.unitPrice || 0) * i.quantity),
        0,
      )
    : null;

  const handleRemove = (productId) => {
    dispatch({ type: 'REMOVE_ITEM', payload: productId });
  };

  const handleProceed = () => {
    navigate('/cart');
  };

  return (
    <div
      className="bg-white border border-n2 sticky top-20 flex flex-col"
      style={{ maxHeight: 'calc(100vh - 120px)' }}
      data-testid="minicart-sidebar"
      role="complementary"
      aria-label="Your cart"
    >
      {/* ── Header ─────────────────────────────────────────── */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-n2 flex-shrink-0">
        <ShoppingCart size={18} className="text-electric-blue" strokeWidth={1.75} />
        <h2 className="text-sm font-sandvik-headline font-normal text-carbon-black flex-1">
          Your cart
        </h2>
        {itemCount > 0 && (
          <span
            className="bg-vibrant-orange text-white text-xs px-1.5 py-0.5 font-sandvik-text font-normal min-w-[20px] text-center"
            aria-label={`${itemCount} ${itemCount === 1 ? 'item' : 'items'} in cart`}
          >
            {itemCount > 99 ? '99+' : itemCount}
          </span>
        )}
      </div>

      {/* ── Scrollable item list ───────────────────────────── */}
      <div className="overflow-y-auto flex-1 px-4 min-h-0">
        {isEmpty ? (
          <EmptyState />
        ) : (
          items.map((item) => (
            <MiniCartItem
              key={item.productId}
              item={item}
              onRemove={handleRemove}
            />
          ))
        )}
      </div>

      {/* ── Footer: subtotal + CTA ─────────────────────────── */}
      {!isEmpty && (
        <div className="border-t border-n2 px-4 py-3 flex-shrink-0">
          {showPrices && subtotal !== null && (
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-n5 font-sandvik-text">Subtotal</span>
              <span
                className="text-sm font-sandvik-text font-normal text-carbon-black"
                data-testid="minicart-subtotal"
              >
                {subtotal.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
          )}
          <button
            type="button"
            onClick={handleProceed}
            className="w-full bg-electric-blue hover:bg-eb-3 text-white py-2.5 text-sm font-sandvik-text font-normal transition-colors"
            data-testid="minicart-proceed"
          >
            Proceed to cart
          </button>
        </div>
      )}
    </div>
  );
}
